import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ContactSelector } from "./ContactSelector";
import { PricingFields } from "./form/PricingFields";
import { leadFormSchema, LeadFormValues } from "./types";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserSelector } from "../common/UserSelector";
import { Textarea } from "@/components/ui/textarea";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";

interface LeadFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  initialData?: any;
}

const LEAD_STAGES = [
  { value: "new", label: "New" },
  { value: "contact_established", label: "Contact Established" },
  { value: "first_meeting", label: "First Meeting" },
  { value: "negotiating", label: "Negotiating" },
  { value: "closed_won", label: "Closed Won" },
  { value: "closed_lost", label: "Closed Lost" },
  { value: "on_hold", label: "On Hold" },
];

export const LeadForm = ({ onSuccess, onCancel, initialData }: LeadFormProps) => {
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();
  const { session } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<LeadFormValues>({
    resolver: zodResolver(leadFormSchema),
    defaultValues: {
      contact_id: initialData?.contact_id || "",
      stage: initialData?.stage || "new",
      next_follow_up: initialData?.next_follow_up || "",
      name: initialData?.name || "",
      pricing_type: initialData?.pricing_type || "flat",
      price: initialData?.price || 0,
      payment_date: initialData?.payment_date || "",
      recurring_start_date: initialData?.recurring_start_date || "",
      recurring_end_date: initialData?.recurring_end_date || "",
      assigned_to: initialData?.assigned_to || "",
      details: initialData?.details || "",
    },
  });

  const handleSubmit = async (values: LeadFormValues, createAnother: boolean = false) => {
    if (!currentOrganization?.id) {
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      const processFieldValue = (value: string | undefined | null) => {
        if (value === "none" || value === "") {
          return null;
        }
        return value;
      };
      
      const cleanedValues = {
        contact_id: processFieldValue(values.contact_id),
        stage: values.stage,
        next_follow_up: values.next_follow_up || null,
        name: values.name,
        pricing_type: values.pricing_type,
        price: values.price,
        payment_date: values.payment_date || null,
        recurring_start_date: values.recurring_start_date || null,
        recurring_end_date: values.recurring_end_date || null,
        assigned_to: processFieldValue(values.assigned_to),
        details: values.details || null,
      };

      if (initialData?.id) {
        const { data, error } = await supabase.functions.invoke("lead-operations", {
          body: {
            action: "update",
            lead: cleanedValues,
            leadId: initialData.id
          }
        });

        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Lead updated successfully",
        });
        
        onSuccess?.();
      } else {
        const { data, error } = await supabase.functions.invoke("lead-operations", {
          body: {
            action: "create",
            lead: cleanedValues,
            userId: session?.user?.id,
            organizationId: currentOrganization.id
          }
        });

        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Lead created successfully",
        });

        if (createAnother) {
          form.reset();
        } else {
          onSuccess?.();
        }
      }
    } catch (error) {
      console.error("Error saving lead:", error);
      toast({
        title: "Error",
        description: "Failed to save lead",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentOrganization) {
    return <div>Please select an organization to manage leads.</div>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((values) => handleSubmit(values, false))} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Lead Name *</FormLabel>
              <FormControl>
                <Input placeholder="Enter lead name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <ContactSelector form={form} />

        <UserSelector form={form} />

        <FormField
          control={form.control}
          name="stage"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Stage</FormLabel>
              <Select 
                onValueChange={field.onChange} 
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a stage" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {LEAD_STAGES.map((stage) => (
                    <SelectItem key={stage.value} value={stage.value}>
                      {stage.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="next_follow_up"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Next Follow-up</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="details"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Details</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Enter any additional details about this lead..."
                  className="min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <PricingFields form={form} />

        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <div className="flex gap-2 ml-auto">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : initialData ? "Save Changes" : "Create Lead"}
            </Button>
            {!initialData && (
              <Button 
                type="button" 
                disabled={isSubmitting}
                onClick={() => form.handleSubmit((values) => handleSubmit(values, true))()}
              >
                {isSubmitting ? "Saving..." : "Save & Create Another"}
              </Button>
            )}
          </div>
        </div>
      </form>
    </Form>
  );
};
